import * as React from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import fullscreenExit from 'assets/fullscreen_exit_button.svg';
import { useVisualViewport } from '../../utils/use-visual-viewport';
import { useBodyScrollLock } from '../../utils/scroll-lock';
import './MobileTakeover.scss';

export type MobileTakeoverProps = {
    /** Covers the visual viewport from document.body while true. */
    active: boolean;
    /** Exit toggle, Escape. */
    onExit: () => void;
    /** Element whose inline `--*` custom properties (theme vars) the host copies on activate. */
    varsSource?: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
};

const HOST_ACTIVE_CLASSES = ['rw-widget-embedded', 'rw-full-screen', 'rw-takeover'];
const Z_INDEX = '2147483647';

function copyCustomProperties(from: HTMLElement | null | undefined, to: HTMLElement) {
    if (!from) return;
    for (let i = 0; i < from.style.length; i += 1) {
        const name = from.style[i];
        if (name.startsWith('--')) {
            to.style.setProperty(name, from.style.getPropertyValue(name));
        }
    }
}

/**
 * Mobile fullscreen takeover for embedded mode. The children are always
 * rendered through a portal into ONE host element that is created once and
 * physically moved between the inline slot and document.body — re-targeting
 * a portal would remount the conversation (losing the draft and scroll
 * timers), moving the node does not. On body the host escapes the host
 * page's overflow:hidden / transformed ancestors and is sized from the
 * visual viewport so the composer sits above the on-screen keyboard.
 */
const MobileTakeover: React.FC<MobileTakeoverProps> = ({
    active,
    onExit,
    varsSource,
    children,
}) => {
    const { t } = useTranslation();
    const slotRef = useRef<HTMLDivElement>(null);
    const hostRef = useRef<HTMLDivElement | null>(null);
    if (hostRef.current === null && typeof document !== 'undefined') {
        const host = document.createElement('div');
        host.className = 'rw-takeover-host';
        hostRef.current = host;
    }
    const host = hostRef.current;

    const viewport = useVisualViewport(active);
    useBodyScrollLock(active);

    // Place the host: inline slot while inactive, document.body while active.
    useLayoutEffect(() => {
        if (!host) return undefined;
        if (active) {
            copyCustomProperties(varsSource?.current, host);
            host.classList.add(...HOST_ACTIVE_CLASSES);
            host.setAttribute('data-lenis-prevent', '');
            host.style.zIndex = Z_INDEX;
            // Moving the host blurs a focused descendant (the takeover is
            // triggered by focusing the composer); put focus back.
            const focused = document.activeElement;
            document.body.appendChild(host);
            if (focused instanceof HTMLElement && host.contains(focused)) {
                focused.focus({ preventScroll: true });
            }
        } else {
            host.classList.remove(...HOST_ACTIVE_CLASSES, 'rw-keyboard-open');
            host.removeAttribute('data-lenis-prevent');
            host.style.removeProperty('top');
            host.style.removeProperty('height');
            host.style.removeProperty('bottom');
            host.style.removeProperty('z-index');
            slotRef.current?.appendChild(host);
        }
        return undefined;
    }, [active, host, varsSource]);

    // On a first mount that is already active, the parent's ref is attached
    // only after this component's layout effects ran, so retry once refs exist.
    useEffect(() => {
        if (host && active) copyCustomProperties(varsSource?.current, host);
    }, [active, host, varsSource]);

    // Keep the slot populated on first mount (before any activate/deactivate).
    useLayoutEffect(() => {
        if (host && !host.parentElement) slotRef.current?.appendChild(host);
        return () => {
            host?.remove();
        };
    }, [host]);

    // Keyboard open: measured visual viewport (iOS never shrinks the layout
    // viewport for the keyboard). Keyboard closed: inset 0/0, which the
    // browser resolves against the current layout viewport, so Chrome iOS's
    // auto-hiding toolbar is handled without any (stale) measurement.
    useLayoutEffect(() => {
        if (!host || !active) return;
        if (viewport.keyboardOpen) {
            host.style.top = `${viewport.top}px`;
            host.style.height = `${viewport.height}px`;
            host.style.bottom = 'auto';
        } else {
            host.style.top = '0px';
            host.style.bottom = '0px';
            host.style.removeProperty('height');
        }
        host.classList.toggle('rw-keyboard-open', viewport.keyboardOpen);
    }, [host, active, viewport]);

    useEffect(() => {
        if (!active) return undefined;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !e.defaultPrevented) onExit();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [active, onExit]);

    if (!host) return <>{children}</>;

    return (
        <div ref={slotRef} className="rw-takeover-slot">
            {createPortal(
                <>
                    {active && (
                        <div className="rw-takeover-bar">
                            <button
                                type="button"
                                className="rw-takeover-exit"
                                onClick={onExit}
                                aria-label={t('takeover.exitLabel')}
                            >
                                <img src={fullscreenExit} alt="" />
                            </button>
                        </div>
                    )}
                    {children}
                </>,
                host
            )}
        </div>
    );
};

export default MobileTakeover;
